/**
 * LandingShell — Wraps the marketing landing pages (Home / Student / Parent / School).
 *
 * Responsibilities:
 *   - Inject the design-system CSS (`/landing-v2/style.css`) into <head> on mount,
 *     remove on unmount → keeps marketing styles out of the rest of Yukti.
 *   - Inject Google Fonts (Outfit + Plus Jakarta Sans + Poppins) once.
 *   - Render the shared header (logo + nav + Contact CTA + mobile hamburger).
 *   - Render the shared footer (brand + links + copyright).
 *   - Render the 3 ambient glow-orbs from the original design.
 *
 * Pages plug children in between header and footer. `activeNav` highlights the
 * current page in the top nav.
 *
 * Phase 1: static markup. Phase 2 will wire the mobile menu toggle + gimmick
 * badge interactivity.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Landing v2 styles + fonts are loaded eagerly in `public/index.html` to
// eliminate FOUC. This component is purely structural — header + footer +
// glow orbs — and adds the `.yukti-landing-v2` wrapper class that activates
// the scoped landing styles.

export default function LandingShell({ children, activeNav }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Routes adapt to the current base path:
  //  /landing-v2 (staging) → nav uses /landing-v2/student, /landing-v2/parent…
  //  / (post-cutover)      → nav uses /student, /parent…
  const stagingMode = location.pathname.startsWith('/landing-v2');
  const basePath = stagingMode ? '/landing-v2' : '';
  const navItems = [
    { key: 'home',    label: 'What is Yukti', to: basePath || '/' },
    { key: 'student', label: 'Student',       to: `${basePath}/student` },
    { key: 'parent',  label: 'Parent',        to: `${basePath}/parent` },
    { key: 'school',  label: 'School',        to: `${basePath}/school` },
  ];

  return (
    <div className="yukti-landing-v2">
      {/* Ambient background glow orbs */}
      <div className="glow-orb orb-1" />
      <div className="glow-orb orb-2" />
      <div className="glow-orb orb-3" />

      {/* HEADER */}
      <header id="main-header">
        <div className="header-container">
          <Link
            to={basePath || '/'}
            className="logo"
            id="header-logo-link"
            aria-label="Yukti home"
          >
            <img
              src="/landing-v2/assets/logo.webp"
              alt="Yukti Logo"
              className="logo-img"
            />
            <span>Yukti</span>
          </Link>
          <nav
            id="navbar"
            className={mobileMenuOpen ? 'active' : ''}
            data-testid="landing-nav"
          >
            <ul className="nav-links">
              {navItems.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    className={`nav-link${activeNav === item.key ? ' active' : ''}`}
                    data-testid={`nav-link-${item.key}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className="nav-actions">
            <a
              href="#contact"
              className="btn btn-primary glow-btn"
              data-testid="contact-cta"
            >
              Contact Us
            </a>
          </div>
          <button
            className={`mobile-toggle${mobileMenuOpen ? ' active' : ''}`}
            aria-label="Toggle navigation menu"
            onClick={() => setMobileMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            type="button"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
        </div>
      </header>

      {/* PAGE CONTENT */}
      <main>{children}</main>

      {/* FOOTER */}
      <footer id="main-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="logo">
                <img
                  src="/landing-v2/assets/logo.webp"
                  alt="Yukti Logo"
                  className="logo-img-footer"
                />
                <span>Yukti</span>
              </div>
              <p>
                AI-native learning assistant tool. NCERT-aligned. Built for
                India&apos;s Class 6–12.
              </p>
            </div>

            <div className="footer-links-col">
              <h4>Company</h4>
              <ul>
                <li><a href="#" data-testid="footer-about">About Us</a></li>
                <li><a href="#contact" data-testid="footer-contact">Contact</a></li>
                <li><a href="#" data-testid="footer-privacy">Privacy Policy</a></li>
                <li><a href="#" data-testid="footer-terms">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              © 2026 Yukti Learning. Serving CBSE, ICSE, and State Board students
              across India.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
